import type { WorkspaceState } from '../types/workspace';
import type { Asset } from './legacyCompiler';
import { compilerContext, compilePromptRecords, type PreparedPlan } from './promptCompiler';
import { logActivity } from './activityLogger';

export async function imageDataUrl(url:string):Promise<string> {
  if (/^data:image\/(png|jpeg|webp);base64,/.test(url)) return url;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Cannot read product photo (${response.status}).`);
  const blob = await response.blob();
  if (!['image/png','image/jpeg','image/webp'].includes(blob.type)) throw new Error('Use PNG, JPEG, or WebP photos.');
  return new Promise((resolve,reject) => {
    const reader = new FileReader(); reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result)); reader.readAsDataURL(blob);
  });
}

export async function callShaper(workspace:WorkspaceState):Promise<PreparedPlan> {
  const startTime = Date.now();
  const sourceUrls = workspace.productImageUrls?.length ? workspace.productImageUrls : workspace.productImageUrl ? [workspace.productImageUrl] : [];
  if (!workspace.productReady || !sourceUrls.length) throw new Error('Add a product photo.');
  const products:Asset[] = await Promise.all(sourceUrls.slice(0, 8).map(async (url, index) => ({id:index === 0 ? 'product-main' : `product-${index + 1}`,name:`${workspace.productName} photo ${index + 1}`,data:await imageDataUrl(url)})));
  const {input,template,engine} = compilerContext(workspace,products,workspace.referenceImages || []);
  const payload = engine.buildShaperPayload(template);
  payload.contents[0].parts.push({text:`Operator's ordered image cards (platform rules take precedence): ${JSON.stringify(workspace.plan.filter(slot=>slot.enabled))}`});
  let plan = engine.buildFallbackPlan(template);
  let warning:string|undefined;
  try {
    for (let attempt=0;attempt<2;attempt++) {
      const response = await fetch('/api/shape',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      void logActivity('api', 'shaper_request', { success: response.ok, status: response.status, latencyMs: Date.now() - startTime, platformId: workspace.platform.id });
      if (!response.ok) throw new Error(`Shaper request failed (${response.status}).`);
      const body = await response.json();
      const candidate = body.candidates?.[0];
      const raw = candidate?.content?.parts?.filter((part:{text?:string;thought?:boolean})=>part.text && !part.thought).map((part:{text:string})=>part.text).join('') || body.text;
      const validation = engine.validateRawShaperPlan(raw,template);
      if (validation.valid && candidate?.finishReason === 'STOP') { plan = engine.validateShaperPlan(raw,template); break; }
      if (attempt === 1) {
        // Keep the request usable when the model returns a structurally valid
        // plan with a contract violation (most commonly authored copy marked
        // as overlay). validateShaperPlan filters invalid authored items and
        // repairs the remaining fields against the platform fallback, so a
        // second failed response must not prevent image generation.
        plan = engine.validateShaperPlan(raw,template);
        break;
      }
    }
  } catch (error) { void logActivity('error', 'shaper_failed', { error: error instanceof Error ? error.message : 'Unknown', latencyMs: Date.now() - startTime }); warning = error instanceof Error ? error.message : 'Shaper unavailable'; }
  // Persist the resolved seed photo, not an ephemeral URL.
  const snapshot = {...workspace,productImageUrl:products[0].data,productImageUrls:products.map(product => product.data)};
  return {workspace:snapshot,input,template,plan,prompts:compilePromptRecords(snapshot,input,template,plan),warning};
}
